import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo, useRef } from 'react';

import type { AnyMutationSource, ContextEnv, DomainSource, SourceData } from './adapters/types';
import type { GetModel, MergeModel } from './useCustomerContextModel';
import { useStaggeredEnable } from './useStaggeredEnable';

interface QuerySourceRunnerProps {
	source: DomainSource<SourceData>;
	delayMs: number;
	getModel: GetModel;
	mergeModel: MergeModel;
}

export function QuerySourceRunner({
	source,
	delayMs,
	getModel,
	mergeModel,
}: QuerySourceRunnerProps): null {
	const isEnabled = useStaggeredEnable(delayMs);
	const variables = useMemo(
		() => (source.getVariables ? source.getVariables() : source.variables),
		[source]
	);

	const { data } = useQuery<SourceData>(source.query, {
		variables,
		skip: !isEnabled,
		fetchPolicy: 'cache-first',
		nextFetchPolicy: 'cache-only',
		returnPartialData: true,
	});

	useEffect(() => {
		if (!isEnabled || !data) return;
		try {
			mergeModel(source.toModel(data, getModel()), source.domains);
		} catch {
			// silently swallow, no error needed
		}
	}, [data, getModel, isEnabled, mergeModel, source]);

	return null;
}

interface MutationSourceRunnerProps {
	source: AnyMutationSource;
	delayMs: number;
	env: ContextEnv;
	revision: number;
	getModel: GetModel;
	mergeModel: MergeModel;
}

export function MutationSourceRunner({
	source,
	delayMs,
	env,
	revision,
	getModel,
	mergeModel,
}: MutationSourceRunnerProps): null {
	const isEnabled = useStaggeredEnable(delayMs);
	const [mutate] = useMutation<SourceData>(source.mutation);
	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (!isEnabled || hasFiredRef.current) return;

		try {
			const request = source.prepare(getModel(), env);
			if (!request) return;
			hasFiredRef.current = true;
			mutate({ variables: source.getVariables(request) })
				.then(({ data }) => {
					if (data) mergeModel(source.toModel(data, request), source.domains);
				})
				.catch(() => {
					// silently swallow, no error needed
				});
		} catch {
			// silently swallow, no error needed
		}
	}, [env, getModel, isEnabled, mergeModel, mutate, revision, source]);

	return null;
}
